// @flow

import * as React from 'react';
import debounce from 'lodash/debounce';
import classNames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import { localize } from '@ta/common.i18n';
import { useUpdatedMember } from '@ta/social.member-common';
import { Icon } from '@ta/styleguide.icon';
import type { ButtonProps } from '@ta/styleguide.button';
import Button from '@ta/styleguide.button';
import type { Profile } from './FollowAction';
import FollowAction, { type GatedFollowAction } from './FollowAction';
import styles from './FollowButton.less';

type ExternalProps = {|
  /** accept all props that Button does, except for onClick (which is replaced by onToggleFollow) */
  ...$Diff<ButtonProps, {| onClick?: (event: SyntheticUIEvent<*>) => mixed |}>,
  /** Prevent clicks on this button from propagating further */
  +preventClickPropagation?: boolean,
  /** member profile object */
  +profile: Profile,
  /** display type for the button */
  +displayType: 'button' | 'button+icon' | 'icon+text',
  /** disable toast notifications on follow/unfollow */
  disableToast?: boolean,
  /** callback fired when the follow button is toggled. */
  onToggleFollow?: (interactionType: 'FOLLOW' | 'UNFOLLOW') => void,
  /** Styling update: true will change to a non-green variant of the follow button. */
  greenless?: boolean,
|};

type Props = {|
  /** Don't need disableToast */
  ...$Diff<ExternalProps, {| disableToast: boolean | void |}>,
  /** provided by FollowAction */
  +gatedAction: GatedFollowAction,
|};

const FollowButton = (props: Props) => {
  const {
    displayType,
    profile,
    gatedAction,
    onToggleFollow,
    preventClickPropagation,
    greenless,
    ...buttonProps
  } = props;

  /** Tracks the local state of the follow button's active state for a snappier experience. */
  const [localCache, setLocalCache] = React.useState(null);
  const currentGatedAction = React.useRef(gatedAction);
  const currentToggleFollow = React.useRef(onToggleFollow);

  React.useEffect(() => {
    currentGatedAction.current = gatedAction;
  }, [gatedAction]);

  React.useEffect(() => {
    currentToggleFollow.current = onToggleFollow;
  }, [onToggleFollow]);

  const updatedFollowStatus = useUpdatedMember(profile).isFollowing;

  React.useEffect(() => {
    setLocalCache(null);
  }, [updatedFollowStatus]);

  const isFollowing = localCache === null ? updatedFollowStatus : localCache;

  // only make a service call on leading and trailing edge since service calls take significant CPU time and toast creation causes significant layout thrashing
  const debouncedGatedAction = React.useCallback(
    debounce(
      interactionType => {
        if (currentToggleFollow.current) {
          currentToggleFollow.current(interactionType);
        }
        currentGatedAction.current();
      },
      2000,
      {
        leading: true,
        trailing: true,
      },
    ),
    [],
  );

  const onClick = React.useCallback(
    (e: SyntheticUIEvent<*>) => {
      if (e && e.detail > 0 && e.currentTarget) {
        e.currentTarget.blur();
      }
      setLocalCache(!isFollowing);
      const interactionType = isFollowing ? 'UNFOLLOW' : 'FOLLOW';
      debouncedGatedAction(interactionType);
      if (preventClickPropagation) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    [preventClickPropagation, isFollowing, debouncedGatedAction],
  );

  if (displayType === 'icon+text') {
    return (
      <span
        className={classNames(styles.icon_text, {
          [styles.active]: isFollowing,
          [styles.greenless]: greenless,
        })}
        onClick={onClick}
      >
        {<Icon name="add-friend-fill" className={styles.icon} />}
        {/* Unfollow / Follow */}
        <span className={classNames('ui_link', styles.text)}>
          {isFollowing ? localize('social_nowFollowing') : localize('social_Follow')}
        </span>
      </span>
    );
  }

  const contents = (
    <span className={styles.text_section}>
      {displayType === 'button+icon' && <Icon className={styles.icon} name="add-friend-fill" />}
      <span className={displayType === 'button+icon' ? styles.text : null}>
        {isFollowing ? localize('social_nowFollowing') : localize('social_Follow')}
      </span>
    </span>
  );

  return greenless ? (
    <button
      className={classNames('ui_button', styles.greenless, {
        [styles.isFollowing]: isFollowing,
        fullwidth: buttonProps.size === 'fullwidth',
      })}
      onClick={onClick}
      {...buttonProps}
    >
      {contents}
    </button>
  ) : (
    <Button {...buttonProps} type={isFollowing ? 'primary' : 'secondary'} onClick={onClick}>
      {contents}
    </Button>
  );
};

const FollowButtonWithToast = (props: ExternalProps) => {
  const { disableToast, ...otherProps } = props;
  /* FAST-FOLLOW: We should be smarter about this. Mark as disabled instead of removing? Working w/ Design on approach to UX. Reuse DisabledSocialAction for guidance. */
  return (
    featureIsEnabled('social_follow_enabled') && (
      <FollowAction disableToast={disableToast} profile={otherProps.profile}>
        {gatedAction => <FollowButton {...otherProps} gatedAction={gatedAction} />}
      </FollowAction>
    )
  );
};

FollowButtonWithToast.defaultProps = {
  greenless: false,
};

export default FollowButtonWithToast;
